import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Mail, MessageSquare, Trash2, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    message: "",
    contact_info: "",
  });

  // Delete request state
  const [deleteRequestId, setDeleteRequestId] = useState("");
  const [deleteRequestCode, setDeleteRequestCode] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.message || formData.message.length < 10) {
      toast.error("Please enter a message (at least 10 characters)");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRequest = async (e) => {
    e.preventDefault();

    if (!deleteRequestId || !deleteRequestCode) {
      toast.error("Please enter both your request ID and code");
      return;
    }

    setDeleteLoading(true);

    try {
      const response = await fetch(
        `${API}/requests/${deleteRequestId}?code=${deleteRequestCode}`,
        { method: "DELETE" }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to delete");
      }

      toast.success("Your information has been deleted");
      setDeleteRequestId("");
      setDeleteRequestCode("");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="bg-[color:var(--pws-off)] min-h-[calc(100vh-80px)] py-12 md:py-16">
      <div className="container-pws">
        <div className="max-w-2xl mx-auto">
          <h1
            className="font-['Space_Grotesk'] text-3xl sm:text-4xl font-semibold text-[color:var(--pws-charcoal)] tracking-tight"
            data-testid="contact-title"
          >
            Contact Us
          </h1>
          <p className="mt-3 text-[color:var(--pws-stone)]">
            You can contact us without sharing your legal name.
          </p>

          {/* Contact Form */}
          <Card className="mt-8 border-[color:var(--pws-line)] bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[color:var(--pws-charcoal)]">
                <MessageSquare className="h-5 w-5 text-[color:var(--pws-plum)]" />
                Send a Message
              </CardTitle>
            </CardHeader>
            <CardContent>
              {submitted ? (
                <div className="text-center py-8" data-testid="contact-success">
                  <CheckCircle className="h-12 w-12 mx-auto text-[color:var(--pws-success)]" />
                  <p className="mt-4 text-[color:var(--pws-ink)]">
                    Thank you for your message. We'll respond if needed.
                  </p>
                  <Button
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ message: "", contact_info: "" });
                    }}
                    variant="outline"
                    className="mt-4 border-[color:var(--pws-line)]"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div className="space-y-2">
                    <Label htmlFor="message">Your Message</Label>
                    <Textarea
                      id="message"
                      placeholder="How can we help?"
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                      className="min-h-[120px] border-[color:var(--pws-line)]"
                      data-testid="contact-message-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_info">
                      How to reach you{" "}
                      <span className="text-[color:var(--pws-stone)]">(optional)</span>
                    </Label>
                    <Input
                      id="contact_info"
                      placeholder="Email, phone, or other (optional)"
                      value={formData.contact_info}
                      onChange={(e) =>
                        setFormData({ ...formData, contact_info: e.target.value })
                      }
                      className="border-[color:var(--pws-line)]"
                      data-testid="contact-info-input"
                    />
                    <p className="text-xs text-[color:var(--pws-stone)]">
                      Anonymous messages are welcome.
                    </p>
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-[color:var(--pws-plum)] hover:bg-[color:var(--pws-plum-hover)] text-white"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="mr-2 h-4 w-4" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Delete Info Section */}
          <Card className="mt-8 border-[color:var(--pws-line)] bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[color:var(--pws-charcoal)]">
                <Trash2 className="h-5 w-5 text-[color:var(--pws-danger)]" />
                Delete My Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[color:var(--pws-stone)] mb-4">
                If you previously submitted a request and want to delete your
                information, enter your request code below.
              </p>
              <form onSubmit={handleDeleteRequest} className="space-y-4" data-testid="delete-form">
                <div className="space-y-2">
                  <Label htmlFor="delete_code">Request Code</Label>
                  <Input
                    id="delete_code"
                    placeholder="Enter your request code"
                    value={deleteRequestCode}
                    onChange={(e) => setDeleteRequestCode(e.target.value)}
                    className="border-[color:var(--pws-line)]"
                    data-testid="delete-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="delete_id">Request ID (optional)</Label>
                  <Input
                    id="delete_id"
                    placeholder="Enter your request ID if you have it"
                    value={deleteRequestId}
                    onChange={(e) => setDeleteRequestId(e.target.value)}
                    className="border-[color:var(--pws-line)]"
                    data-testid="delete-id-input"
                  />
                </div>
                <Button
                  type="submit"
                  variant="outline"
                  disabled={deleteLoading}
                  className="border-[color:var(--pws-danger)] text-[color:var(--pws-danger)] hover:bg-[color:var(--pws-danger)] hover:text-white"
                  data-testid="delete-submit-btn"
                >
                  {deleteLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete My Information
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
